import React from "react";

/**
 * DAQ-1 Sub-Phase 3 · Eligibility badges + reason chips.
 *
 * All rendering derives from the eligibility payload returned by
 * `DriverEligibilityService`. NO local date math or precedence logic.
 * The UI is a thin renderer over `overall_status`, `block_reasons`,
 * `warning_reasons`, and the per-facet fields.
 */

// Status → visual style. Never collapses distinct states into "Not Available".
const STATUS_STYLES = {
  ELIGIBLE: {
    label: "Eligible",
    cls: "bg-touring-lime/20 text-carbon border-touring-lime/60",
  },
  AVAILABILITY_UNKNOWN: {
    label: "Availability Unknown",
    cls: "bg-amber-hold/15 text-amber-hold border-amber-hold/45",
  },
  ASSIGNMENT_CONFLICT: {
    label: "Assignment Conflict",
    cls: "bg-maintenance-orange/15 text-maintenance-orange border-maintenance-orange/50",
  },
  UNAVAILABLE: {
    label: "Unavailable",
    cls: "bg-critical-red/10 text-critical-red border-critical-red/40",
  },
  MEDICAL_EXPIRED: {
    label: "Medical Expired",
    cls: "bg-critical-red/10 text-critical-red border-critical-red/40",
  },
  LICENSE_EXPIRED: {
    label: "License Expired",
    cls: "bg-critical-red/10 text-critical-red border-critical-red/40",
  },
  RESTRICTED: {
    label: "DO NOT ASSIGN",
    cls: "bg-critical-red text-white border-critical-red",
  },
  INACTIVE: {
    label: "Driver Inactive",
    cls: "bg-steel/10 text-steel border-steel/40",
  },
};

export function EligibilityStatusBadge({ status, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.AVAILABILITY_UNKNOWN;
  return (
    <span data-testid={testid || `elig-status-${status}`}
          className={`inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10.5px] uppercase tracking-widest font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
}

// Passport is warning-only per Owner Decision 1. Rendered as its own
// small chip so operators can see status distinctly from CDL/medical.
const PASSPORT_STYLES = {
  VALID:          { label: "Passport Valid",     cls: "bg-touring-lime/15 text-carbon border-touring-lime/50" },
  EXPIRING_SOON:  { label: "Passport Expiring",  cls: "bg-amber-hold/15 text-amber-hold border-amber-hold/45" },
  EXPIRED:        { label: "Passport Expired",   cls: "bg-critical-red/10 text-critical-red border-critical-red/40" },
  MISSING:        { label: "Passport Missing",   cls: "bg-steel/10 text-steel border-steel/40" },
};

export function PassportChip({ status }) {
  const s = PASSPORT_STYLES[status] || PASSPORT_STYLES.MISSING;
  return (
    <span className={`inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${s.cls}`}>
      {s.label}
    </span>
  );
}

// Reason chip — used for both block_reasons (hard) and warning_reasons.
export function ReasonChip({ reason, hard }) {
  const cls = hard
    ? "bg-critical-red/10 text-critical-red border-critical-red/40"
    : "bg-amber-hold/12 text-amber-hold border-amber-hold/40";
  return (
    <span title={reason.message}
          className={`inline-flex items-center border rounded-sm px-1.5 py-0.5 text-[10px] uppercase tracking-widest ${cls}`}>
      {reason.code.replace(/_/g, " ")}
    </span>
  );
}

export function EligibilityReasons({ eligibility, compact = false }) {
  const blocks = eligibility?.block_reasons || [];
  const warns = eligibility?.warning_reasons || [];
  if (blocks.length === 0 && warns.length === 0) return null;
  if (compact) {
    return (
      <div className="flex flex-wrap gap-1">
        {blocks.map((r, i) => <ReasonChip key={`b${i}`} reason={r} hard />)}
        {warns.map((r, i) => <ReasonChip key={`w${i}`} reason={r} />)}
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      {blocks.length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-critical-red mb-1">Hard Blocks</div>
          <ul className="space-y-1 text-[12px] text-carbon">
            {blocks.map((r, i) => (
              <li key={`b${i}`} className="flex items-start gap-2">
                <ReasonChip reason={r} hard />
                <span className="text-steel">{r.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {warns.length > 0 && (
        <div>
          <div className="text-[10.5px] uppercase tracking-widest text-amber-hold mb-1">Warnings</div>
          <ul className="space-y-1 text-[12px] text-carbon">
            {warns.map((r, i) => (
              <li key={`w${i}`} className="flex items-start gap-2">
                <ReasonChip reason={r} />
                <span className="text-steel">{r.message}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
